<!--
  The fallback for an entry with no assumed kind: `PostEntryRequest.postings` laid bare,
  each line signed by hand rather than through an expense/income/transfer role. Used when
  a payment does not fit "one money account, one or more categories" - a genuinely mixed
  entry the flow grid has no field shape for. Balance is enforced here, not merely warned
  about: unlike an account's own running balance (AGENTS.md - warn, never block), postings
  summing to zero is a structural requirement of double-entry, not a business-rule guess.
-->
<script setup lang="ts">
import { ActionButton, StatusTag } from '@taler/ui'
import { formatLocalDate, type LocalDate, type Money, parseLocalDate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { GenericLineDraft } from '../internal/generic-draft.ts'
import type { TagOption } from '../internal/tag-options.ts'
import type { Frecency, Target } from '../internal/targets.ts'
import AccountCombobox from '@/features/entries/components/AccountCombobox.vue'
import TagsInput from '@/features/entries/components/TagsInput.vue'

const props = defineProps<{
  date: LocalDate
  memo: string
  tags: readonly string[]
  tagSuggestions: readonly TagOption[]
  lines: GenericLineDraft[]
  allTargets: readonly Target[]
  frecency: ReadonlyMap<string, Frecency>
  isBalanced: boolean
  isComplete: boolean
  balance: Money | undefined
  isSubmitting: boolean
  hasSubmitError: boolean
  submitErrorDetail: string | undefined
  justPosted: boolean
}>()

const emit = defineEmits<{
  'update:date': [LocalDate]
  'update:memo': [string]
  'update:tags': [readonly string[]]
  addLine: []
  removeLine: [lineId: string]
  submit: []
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const targetsById = computed(() => new Map(props.allTargets.map((target) => [target.id, target])))

/** The first line's own fixed-currency target settles the whole entry's currency, the same
 * rule `generic-draft.ts`'s `validateGenericEntry` applies - computed here too, not read off
 * `props.balance`, since that stays undefined until every line is already complete. */
const entryCurrency = computed(() => {
  for (const line of props.lines) {
    const target = line.targetId === undefined ? undefined : targetsById.value.get(line.targetId)
    if (target?.currency !== undefined) return target.currency
  }
  return undefined
})

function onDateChange(value: string): void {
  if (value === '') return
  emit('update:date', parseLocalDate(value))
}

/** The line's own target currency, falling back to the entry-wide one once any line has settled it. */
function lineCurrency(line: GenericLineDraft) {
  const target = line.targetId === undefined ? undefined : targetsById.value.get(line.targetId)
  return target?.currency ?? entryCurrency.value
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-block)]">
    <div class="flex items-center gap-[var(--space-group)]">
      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('entries.date')
        }}</span>
        <input
          type="date"
          :value="formatLocalDate(date)"
          class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[color:var(--color-text)]"
          @change="(event) => onDateChange((event.target as HTMLInputElement).value)"
        />
      </label>
      <label class="flex flex-1 flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('entries.memo')
        }}</span>
        <input
          :value="memo"
          type="text"
          maxlength="100"
          class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[color:var(--color-text)]"
          @input="emit('update:memo', ($event.target as HTMLInputElement).value)"
        />
      </label>
      <label class="flex flex-1 flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('entries.tags')
        }}</span>
        <TagsInput
          id="generic-entry-tags"
          :label="t('entries.tags')"
          :model-value="tags"
          :suggestions="tagSuggestions"
          @update:model-value="(value) => emit('update:tags', value)"
        />
      </label>
    </div>

    <table class="w-full border-collapse bg-[color:var(--color-surface)]">
      <thead>
        <tr
          class="border-b border-[color:var(--color-border)] text-left text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
        >
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('entries.postingAccount') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
            {{ t('entries.signedAmount') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">{{ t('entries.memo') }}</th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            <span class="sr-only">{{ t('entries.rowActions') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="line in lines" :key="line.id" class="border-b border-[color:var(--color-border)]">
          <td class="h-[var(--density-row-tall)] min-w-56 px-[var(--space-cell)] align-middle">
            <AccountCombobox
              :id="`generic-target-${line.id}`"
              :label="t('entries.postingAccount')"
              :model-value="line.targetId"
              :targets="allTargets"
              allow-create-contact
              :frecency="frecency"
              @update:model-value="(value) => (line.targetId = value)"
            />
          </td>
          <td class="h-[var(--density-row-tall)] min-w-40 px-[var(--space-cell)] align-middle">
            <AmountInput
              v-model="line.amount"
              :currency="lineCurrency(line)"
              :locale="currentLocale"
              allow-negative
              :show-total="false"
              :aria-label="t('entries.signedAmount')"
              placeholder="-0.00"
            />
          </td>
          <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
            <input
              v-model="line.memo"
              type="text"
              maxlength="255"
              :aria-label="t('entries.memo')"
              class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[color:var(--color-text)]"
            />
          </td>
          <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
            <button
              v-if="lines.length > 2"
              type="button"
              :aria-label="t('entries.removeSplit')"
              class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] px-[var(--space-tight)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger)]"
              @click="emit('removeLine', line.id)"
            >
              ×
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex items-center justify-between gap-[var(--space-group)]">
      <button
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
        @click="emit('addLine')"
      >
        {{ t('entries.addLine') }}
      </button>
    </div>

    <section
      :aria-label="t('entries.thisEntry')"
      class="flex flex-wrap items-center gap-x-[var(--space-group)] gap-y-[var(--space-cell)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-group)] py-[var(--space-cell)]"
    >
      <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
        <h2 class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text)]">
          {{ t('entries.thisEntry') }}
        </h2>
        <p class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-1)]">
          <span class="text-[color:var(--color-text-muted)]">{{ t('entries.balance') }}</span>
          <span class="font-medium tabular-nums text-[color:var(--color-text)]">
            <MoneyAmount v-if="balance" :value="balance" sign />
            <template v-else>—</template>
          </span>
        </p>
        <StatusTag v-if="isComplete && !isBalanced" tone="danger" size="sm" class="w-fit">
          {{ t('entries.notBalanced') }}
        </StatusTag>
      </div>

      <StatusTag v-if="hasSubmitError" tone="danger" role="alert" class="w-full">
        {{ submitErrorDetail ?? t('entries.submitErrorGeneric') }}
      </StatusTag>
      <StatusTag v-else-if="justPosted" tone="ok">{{ t('entries.posted') }}</StatusTag>

      <ActionButton
        size="lg"
        type="button"
        :disabled="isSubmitting || !isComplete || !isBalanced"
        class="ml-auto shrink-0 font-medium"
        @click="emit('submit')"
      >
        {{ isSubmitting ? t('entries.posting') : t('entries.postEntry') }}
      </ActionButton>
    </section>
    <slot name="aside" />
  </div>
</template>
